/**
 * API Endpoint Manager
 * Manages primary and fallback API URLs with session-based caching
 */

const SESSION_STORAGE_KEY = 'api_endpoint_url';

class ApiEndpointManager {
  private primaryUrl: string;
  private fallbackUrl: string;
  private currentUrl: string | null = null;

  constructor() {
    // Get URLs from environment variables
    this.primaryUrl = process.env.REACT_APP_PRIMARY_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
    this.fallbackUrl = process.env.REACT_APP_FALLBACK_API_URL || this.primaryUrl;
    
    // Try to restore from session storage — only if it looks like a real URL
    const cached = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (cached && /^https?:\/\/.+\..+/.test(cached)) {
      this.currentUrl = cached;
    } else if (cached) {
      // Invalid cached URL (e.g. 'https://dman') — discard it
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }

  /**
   * Format the API URL to ensure it has the correct format
   */
  private formatApiUrl(url: string): string {
    let formattedUrl = url;
    
    // Remove trailing slash
    if (formattedUrl.endsWith('/')) {
      formattedUrl = formattedUrl.slice(0, -1);
    }
    
    // Add /api/v1 if not present
    if (!formattedUrl.endsWith('/api/v1')) {
      if (formattedUrl.includes('/api/v1/')) {
        // Extract the base part before any duplicate /api/v1
        formattedUrl = formattedUrl.split('/api/v1')[0];
      }
      formattedUrl = `${formattedUrl}/api/v1`;
    }
    
    return formattedUrl;
  }

  /**
   * Get the current API endpoint URL
   * Returns primary URL if no URL is cached
   */
  getCurrentUrl(): string {
    if (this.currentUrl) {
      return this.formatApiUrl(this.currentUrl);
    }
    return this.formatApiUrl(this.primaryUrl);
  }

  /**
   * Get the primary API URL
   */
  getPrimaryUrl(): string {
    return this.formatApiUrl(this.primaryUrl);
  }

  /**
   * Get the fallback API URL
   */
  getFallbackUrl(): string {
    return this.formatApiUrl(this.fallbackUrl);
  }

  /**
   * Switch to fallback URL and cache it
   */
  switchToFallback(): void {
    this.currentUrl = this.fallbackUrl;
    sessionStorage.setItem(SESSION_STORAGE_KEY, this.fallbackUrl);
    console.log('Switched to fallback API:', this.formatApiUrl(this.fallbackUrl));
  }

  /**
   * Set and cache the working URL
   */
  setWorkingUrl(url: string): void {
    this.currentUrl = url;
    sessionStorage.setItem(SESSION_STORAGE_KEY, url);
    console.log('Set working API URL:', this.formatApiUrl(url));
  }

  /**
   * Clear the cached endpoint (used on logout)
   */
  clearCache(): void {
    this.currentUrl = null;
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    console.log('Cleared API endpoint cache');
  }

  /**
   * Check if we're currently using the primary URL
   */
  isUsingPrimary(): boolean {
    const current = this.getCurrentUrl();
    const primary = this.getPrimaryUrl();
    return current === primary;
  }

  /**
   * Check if we should try fallback
   * Returns true if we haven't tried fallback yet
   */
  shouldTryFallback(): boolean {
    // Only try fallback if:
    // 1. We have a different fallback URL
    // 2. We're currently using primary (haven't tried fallback yet)
    return this.primaryUrl !== this.fallbackUrl && this.isUsingPrimary();
  }
}

// Export singleton instance
export const apiEndpointManager = new ApiEndpointManager();
